import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import 'react-calendar/dist/Calendar.css';
import GenerateReport from './GenerateReport';
import DataTable from 'react-data-table-component-with-filter';
import '../Appointments/Appointments.css';
import '../Tables.css';
import Sidebar from '../Sidebar';
import InternalHeader from '../InternalHeader';
import CustomPagination from '../CustomPagination/CustomPagination';
import { FaSearch, FaCog, FaArchive } from 'react-icons/fa';
import { format } from 'date-fns';
import Actions from '../Actions/Actions';
import FilterReports from '../Filter/FilterReports';
import AccessControl from '../AccessControl';

const Reports = () => {
    const navigate = useNavigate();
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [reports, setReports] = useState({
        Appointments: [],
        Patients: [],
        Treatments: [],
        Dentists: [],
    });
    const [logs, setLogs] = useState([]); // Logs for tracking user actions
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [showReportModal, setShowReportModal] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedPatients, setSelectedReports] = useState([]); // State for selected patients
    const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);
    const [viewOption, setViewOption] = useState('all'); // Default to 'all'
    const logChange = (action, details) => {
        setLogs(prevLogs => [...prevLogs, { timestamp: new Date().toLocaleString(), action, details }]);
    };


    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/reports');
                if (!response.ok) {
                    throw new Error('Failed to fetch reports');
                }
                const data = await response.json();
                logChange('Fetched', `Loaded ${data.length} reports.`);

                // Organize the data by report type
                const organizedData = {
                    Appointments: data.filter((report) => report.reportType === 'Appointments'),
                    Patients: data.filter((report) => report.reportType === 'Patients'),
                    Treatments: data.filter((report) => report.reportType === 'Treatments'),
                    Dentists: data.filter((report) => report.reportType === 'Dentists'),
                };

                setReports(organizedData); // Update state with organized data
                setError(null);
            } catch (err) {
                setError('Failed to load reports. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleSelectedRowsChange = ({ selectedRows }) => {
        setSelectedRows(selectedRows.map(row => row._id));
        setSelectedReports(selectedRows); // Update selected patients
    };

    const handleActionsToggle = () => {
        setIsActionsDropdownOpen(!isActionsDropdownOpen);
    };

    const handleDeleteSelected = async () => {
        console.log("Selected reports to archive:", selectedRows);

        if (selectedRows.length === 0) return;

        const confirmArchive = window.confirm('Are you sure you want to archive the selected patients?');
        if (!confirmArchive) {
            return;
        }

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/reports', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ reportIds: selectedRows, isArchived: true }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error('Failed to archive reports');
            }

            // Update local state
            setReports((prevReports) => {
                const updatedReports = { ...prevReports };
                Object.keys(updatedReports).forEach((key) => {
                    updatedReports[key] = updatedReports[key].map((report) =>
                        selectedRows.includes(report._id) ? { ...report, isArchived: true } : report
                    );
                });
                return updatedReports;
            });

            setSelectedRows([]); // Clear selected reports after archiving
            alert(result.message);
        } catch (error) {
            setError('Error archiving reports. Please try again.');
        }
    };

    const handleUnarchiveSelected = async () => {
        console.log("Selected reports to unarchive:", selectedRows);

        if (selectedRows.length === 0) return;

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/reports', {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ reportIds: selectedRows, isArchived: false }),
            });

            if (!response.ok) {
                throw new Error('Failed to unarchive reports');
            }

            // Update local state
            setReports((prevReports) => {
                const updatedReports = { ...prevReports };
                Object.keys(updatedReports).forEach((key) => {
                    updatedReports[key] = updatedReports[key].map((report) =>
                        selectedRows.includes(report._id) ? { ...report, isArchived: true } : report
                    );
                });
                return updatedReports;
            });

            setSelectedRows([]); // Clear selected reports after archiving
        } catch (error) {
            setError('Error unarchiving reports. Please try again.');
        }
    };

    const handleCreateReport = async (newReport) => {
        try {
            const response = await fetch('https://api.hgcdental.clinic/api/reports', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newReport),
            });

            if (!response.ok) {
                throw new Error('Failed to create report');
            }

            const createdReport = await response.json();

            // Update the state based on the report type
            setReports((prevReports) => ({
                ...prevReports,
                [newReport.reportType]: [...prevReports[newReport.reportType], createdReport],
            }));

            setShowReportModal(false);
        } catch (error) {
            setError('Error creating report. Please try again.');
        }
    };

    // Combine all report types into a single array
    const allReports = Object.values(reports).flat();

    // Filter the combined data
    const filteredData = allReports.filter((report) => {
        // Filter by search term
        const matchesSearchTerm =
            (report.reportName &&
                report.reportName.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (report.reportType &&
                report.reportType.toLowerCase().includes(searchTerm.toLowerCase()));

        // Filter by view option
        const matchesViewOption =
            viewOption === 'all' ? !report.isArchived : // Exclude archived records by default
                viewOption === 'isArchived' ? report.isArchived : // Include only archived records
                    report.reportType === viewOption && !report.isArchived; // Exclude archived records for other filters

        return matchesSearchTerm && matchesViewOption;
    });
    // Paginate the filtered data
    const paginatedData = filteredData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    // Handle pagination
    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(Number(newRowsPerPage));
        setCurrentPage(1);
    };

    // DataTable column configuration
    const columns = [
        // { name: 'Report Name', selector: (row) => row.reportName, sortable: true, filterable: true },
        { name: 'Report Type', selector: (row) => row.reportType, sortable: true, filterable: true },
        {
            name: 'Date From',
            selector: (row) => row.dateFrom ? format(new Date(row.dateFrom), 'MMMM dd, yyyy') : 'N/A',
            sortable: true, filterable: true
        },
        {
            name: 'Date To',
            selector: (row) => row.dateTo ? format(new Date(row.dateTo), 'MMMM dd, yyyy') : 'N/A',
            sortable: true, filterable: true
        },
        {
            name: 'Generated On',
            selector: (row) => row.generatedOn ? format(new Date(row.generatedOn), 'MMMM dd, yyyy') : 'N/A',
            sortable: true, filterable: true
        },
    ];

    const customStyles = {
        rows: {
            style: {
                borderBottom: '1px solid #ddd',
            },
        },
        headCells: {
            style: {
                backgroundColor: '#007bff',
                color: 'white',
                fontWeight: 'bold',
                padding: '15px 10px',
                textAlign: 'center',
            },
        },
        cells: {
            style: {
                padding: '12px 10px',
                borderRight: '1px solid #ddd',
            },
        },
        pagination: {
            style: {
                display: 'flex',
                justifyContent: 'center',
                padding: '10px',
                backgroundColor: '#f4f4f4',
            },
        },
        sortIcon: {
            style: {
                color: '#007bff',
                fontSize: '18px',
            },
        },
    };

    return (
        <AccessControl>
            <div className={`appointments-page ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
                <InternalHeader moduleName="Reports" toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} />
                <div className="appointments-container">
                    <Sidebar isSidebarVisible={isSidebarVisible} />
                    <div className="appointments-container2">
                        <div>

                        </div>
                        <div className="data-table-header">
                            <button
                                onClick={() => setShowReportModal(true)}
                                className="create-button"
                            >
                                New
                            </button>
                            <GenerateReport
                                isOpen={showReportModal}
                                onRequestClose={() => setShowReportModal(false)}
                                onCreateReport={handleCreateReport}
                            />
                            <div>
                                {/* Conditionally render Filter or Actions dropdown */}
                                {selectedRows.length > 0 ? (
                                    <div className="actions-dropdown-container">
                                        <button onClick={handleActionsToggle} className="actions-button">
                                            <FaCog />  Actions
                                        </button>
                                        {isActionsDropdownOpen && (
                                            <div className="actions-dropdown-options1">
                                                <div onClick={handleDeleteSelected}>
                                                    <FaArchive /> Archive
                                                </div>
                                                <div onClick={handleUnarchiveSelected}>
                                                    <FaArchive /> Unarchive
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <FilterReports
                                        searchText={searchTerm}
                                        setSearchText={setSearchTerm}
                                        viewOption={viewOption}
                                        setViewOption={setViewOption}
                                    />
                                )}
                            </div>
                            <CustomPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                rowsPerPage={rowsPerPage}
                                onPageChange={handlePageChange}
                                onRowsPerPageChange={handleRowsPerPageChange}
                                totalRecords={filteredData.length}
                            />
                        </div>
                        <div className="table-form">
                            <div>
                                <DataTable
                                    title="Reports"
                                    columns={columns}
                                    data={paginatedData}
                                    progressPending={loading}
                                    highlightOnHover
                                    selectableRows
                                    selectableRowsHighlight
                                    striped
                                    // onRowClicked={(row) => navigate(`/reports/${row.id}`)}
                                    customStyles={customStyles}
                                    onSelectedRowsChange={handleSelectedRowsChange}
                                />
                            </div>
                            {error && <div className="error-message">{error}</div>}
                        </div>
                        <div className="log-display-section">
                            <h2>Logs</h2>
                            <div className="log-container">
                                <ul>
                                    {logs.map((log, index) => (
                                        <li key={index}>{log.timestamp} - {log.action}: {log.details}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AccessControl>
    );
};

export default Reports;